import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Lenis from "lenis";
import ShapeOverlayMenu from "../components/Navbar/ShapeOverlayMenu";
import Footer from "../components/Footer/Footer";

const PROJECTS = [
  {
    slug: "fabriclore",
    prefix: "fl",
    name: "Fabric",
    subtitle: "Lore",
    year: "2025",
    tags: "Full-Stack / E-Commerce",
    accent: "#c4b5fd",
    bg: "#1e1030",
    external: false,
  },
  {
    slug: "stay-leisurely",
    prefix: "sl",
    name: "Stay",
    subtitle: "Leisurely",
    year: "2024",
    tags: "Product / Web App",
    accent: "#86efac",
    bg: "#0d2018",
    external: false,
  },
  {
    slug: "pure-energy",
    prefix: "pe",
    name: "Pure",
    subtitle: "Energy",
    year: "2024",
    tags: "Brand / Web Design",
    accent: "#fde68a",
    bg: "#1a1500",
    external: false,
  },
  {
    slug: "agemd",
    prefix: "agemd",
    name: "AgeMD",
    subtitle: "Health",
    year: "2024",
    tags: "Brand / Web Design",
    accent: "#93c5fd",
    bg: "#0d1a2e",
    external: true,
    href: "https://www.agemd.com",
  },
  {
    slug: "lyfsum",
    prefix: "lyfsum",
    name: "Lyfsum",
    subtitle: "Tourism",
    year: "2024",
    tags: "Product / Web App",
    accent: "#f0abfc",
    bg: "#1a0d1e",
    external: true,
    href: "https://www.lyfsum.com",
  },
];

// Images fly in from scattered vertical positions (matching original CodePen values)
function imgInitial(pi, ii) {
  const rows = [
    {
      y: pi % 2 === 0 ? 175 : -120,
      r: pi % 2 === 0 ? -5 : 5,
      z: pi % 2 === 0 ? 3 : 1,
    },
    {
      y: pi % 2 === 0 ? -225 : 300,
      r: pi % 2 === 0 ? 5 : -5,
      z: pi % 2 === 0 ? 1 : 3,
    },
    { y: pi % 2 === 0 ? 300 : -120, r: pi % 2 === 0 ? 5 : 5, z: 3 },
    { y: pi % 2 === 0 ? -100 : 175, r: pi % 2 === 0 ? -5 : -5, z: 1 },
  ];
  return rows[ii];
}

function imgFinal(pi, ii) {
  return {
    y: pi % 2 === 0 ? (ii % 2 === 0 ? 2 : -2) : ii % 2 === 0 ? -2 : 2,
    r: pi % 2 === 0 ? (ii % 2 === 0 ? 2.5 : -2.5) : ii % 2 === 0 ? -2.5 : 2.5,
  };
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const lerp = (a, b, t) => a + (b - a) * t;
const EASE = 0.1; // lerp factor per frame — controls how much values smooth

export default function WorkNew() {
  const containerRef = useRef(null);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent =
      "html, body { scrollbar-width: none; overflow: -moz-scrollbars-none; } html::-webkit-scrollbar, body::-webkit-scrollbar { display: none; width: 0; }";
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    const workSection = root.querySelector("[data-work-section]");
    const cards = Array.from(root.querySelectorAll("[data-work-item]"));

    // 300vh ghost per project drives the scroll progress
    const ghosts = cards.map(() => {
      const g = document.createElement("div");
      g.style.cssText = "width:100%;height:300vh;";
      workSection.appendChild(g);
      return g;
    });

    const parts = cards.map((card) => ({
      card,
      container: card.querySelector("[data-work-container]"),
      bg: card.querySelector("[data-work-bg]"),
      overlay: card.querySelector("[data-work-overlay]"),
      lines: Array.from(card.querySelectorAll("[data-line]")),
      images: Array.from(card.querySelectorAll("[data-work-image]")),
    }));

    // Per-card smoothed state — lerp current → target each frame
    const state = parts.map(() => ({
      inP: 0,
      lineP: 0,
      imgP: 0,
      dimP: 0,
      outP: 0,
    }));

    const isMobile = window.innerWidth < 768;

    // Set initial image positions
    parts.forEach(({ images }, pi) => {
      images.forEach((img, ii) => {
        const p = imgInitial(pi, ii);
        img.style.transform = `translateY(${p.y}vh) rotate(${p.r}deg) scale(0.8)`;
        img.style.zIndex = p.z;
      });
    });

    const vh0 = window.innerHeight;

    // Ghost positions cached after first tick (browser layout is ready by then)
    let ghostTops = null;

    // Lenis for physical scroll smoothing (matches CodePen's Lenis config)
    const lenis = new Lenis({ lerp: 0.08, autoRaf: false });

    let rafId;

    function tick(time) {
      lenis.raf(time);

      // Compute once after browser has laid out the appended ghosts
      if (!ghostTops) {
        ghostTops = ghosts.map(
          (g) => g.getBoundingClientRect().top + window.scrollY,
        );
      }

      const scrollY = window.scrollY;
      const vh = vh0;
      const GH = 3 * vh;

      parts.forEach(({ card, container, bg, overlay, lines, images }, pi) => {
        const ghostTop = ghostTops[pi];
        const isLast = pi === parts.length - 1;
        const pct = (x) => ghostTop + x * GH;

        // ── Compute targets from raw scroll ──
        const tInP = clamp((scrollY - (ghostTop - vh)) / (2 * vh), 0, 1);
        const tLineP = clamp(
          (scrollY - pct(-0.25)) / (pct(0.15) - pct(-0.25)),
          0,
          1,
        );
        const tImgP = clamp(
          (scrollY - pct(0.05)) / (pct(0.5) - pct(0.05)),
          0,
          1,
        );
        const tDimP = clamp(
          (scrollY - pct(0.2)) / (pct(0.55) - pct(0.2)),
          0,
          1,
        );
        const tOutP = clamp((scrollY - (ghostTop + GH - vh)) / (2 * vh), 0, 1);

        // ── Lerp current → target ──
        const s = state[pi];
        s.inP = lerp(s.inP, tInP, EASE);
        s.lineP = lerp(s.lineP, tLineP, EASE);
        s.imgP = lerp(s.imgP, tImgP, EASE);
        s.dimP = lerp(s.dimP, tDimP, EASE);
        s.outP = lerp(s.outP, tOutP, EASE);

        // Skip DOM writes for desktop cards fully off-screen (mobile writes are cheap enough to always run)
        if (
          !isMobile &&
          tInP === 0 &&
          s.inP < 0.001 &&
          tOutP === 0 &&
          s.outP < 0.001
        )
          return;

        const { inP, lineP, imgP, dimP, outP } = s;

        // ── Apply to DOM ──

        if (isMobile) {
          // Mobile: slide up from below (translateY) — avoids clip-path compositing bugs on iOS
          card.style.transform = `translateY(calc(${(1 - inP) * 100}vh - ${40 * outP}vh))`;
          overlay.style.opacity = lerp(0, 0.85, outP);
        } else {
          // Desktop: horizontal clip-path wipe + parallax
          card.style.clipPath = `inset(0 0% 0 ${(1 - inP) * 100}%)`;
          card.style.transform = isLast
            ? `translateY(${-40 * outP}%)`
            : `translateX(${-40 * outP}%)`;
          container.style.transform = `translateX(${40 * (1 - inP)}%)`;
          bg.style.transform = `scale(${lerp(1.2, 1, inP)})`;
          bg.style.filter = `brightness(${lerp(1, 0.2, dimP)}) blur(${lerp(0, 7.5, dimP)}px)`;
          overlay.style.opacity = lerp(0, 0.85, outP);
        }

        // Text lines slide in from above/below
        lines.forEach((line, li) => {
          const startY = li === 0 ? 125 : -125;
          line.style.transform = `translateY(${lerp(startY, 0, lineP)}%)`;
          line.style.opacity = lerp(0.05, 0.9, lineP);
        });

        images.forEach((img, ii) => {
          const from = imgInitial(pi, ii);
          const to = imgFinal(pi, ii);
          img.style.transform = `translateY(${lerp(from.y, to.y, imgP)}vh) rotate(${lerp(from.r, to.r, imgP)}deg) scale(${lerp(0.8, 1, imgP)})`;
        });
      });

      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      ghosts.forEach((g) => g.parentNode?.removeChild(g));
      parts.forEach(({ card, container, bg, overlay, lines, images }) => {
        card.style.clipPath = "";
        card.style.transform = "";
        container.style.transform = "";
        bg.style.transform = "";
        bg.style.filter = "";
        overlay.style.opacity = "";
        lines.forEach((l) => {
          l.style.transform = "";
          l.style.opacity = "";
        });
        images.forEach((img) => {
          img.style.transform = "";
          img.style.zIndex = "";
        });
      });
    };
  }, []);

  const BG = "var(--home-bg)";
  const PRIMARY = "var(--home-primary)";

  return (
    <div
      ref={containerRef}
      className="wn-root"
      style={{
        background: BG,
        fontFamily: "'General Sans', system-ui, sans-serif",
      }}
    >
      <style>{`
        .wn-root { scrollbar-width: none; }
        .wn-root::-webkit-scrollbar { display: none; }
        .wn-card {
          position: fixed !important;
          top: 0; left: 0;
          width: 100vw; height: 100vh;
          pointer-events: none;
          clip-path: inset(0 0% 0 100%);
          z-index: 2;
          overflow: hidden;
          transition: none !important;
        }
        @media (max-width: 767px) {
          .wn-card { clip-path: none !important; transform: translateY(100vh); }
        }
        .wn-container {
          position: relative;
          width: 100%; height: 100%;
          overflow: hidden;
        }
        .wn-bg {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }
        .wn-images {
          position: absolute;
          inset: 0 auto 0 0;
          width: 100%;
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
        }
        .wn-image-col {
          width: 21vw;
          height: 100%;
          position: relative;
        }
        .wn-image-frame {
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 25vw;
          aspect-ratio: 6/4;
          padding: 0.35em;
          background: rgba(255,255,255,0.75);
        }
        .wn-image-frame img {
          width: 100%; height: 100%; object-fit: cover; display: block;
        }
        .wn-circle-btn {
          position: absolute;
          bottom: 2.5rem; left: 4vw;
          top: auto; right: auto;
          transform: none;
          z-index: 3; pointer-events: auto;
          width: clamp(52px, 5vw, 72px); height: clamp(52px, 5vw, 72px);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          text-decoration: none;
        }
        @media (max-width: 767px) {
          .wn-circle-btn {
            bottom: auto; left: auto;
            top: 50%; right: 4vw;
            transform: translateY(-50%);
          }
        }
        @media (max-width: 767px) {
          .wn-images {
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }
          .wn-image-col {
            width: 100%;
            height: 16vh;
          }
          .wn-image-frame {
            width: 52vw;
            aspect-ratio: 4/3;
          }
        }
      `}</style>

      <Helmet>
        <title>Work — Shreeram Ganeshan</title>
      </Helmet>
      <ShapeOverlayMenu />

      {/* Fixed project cards */}
      {PROJECTS.map((project, index) => (
        <div
          key={project.slug}
          data-work-item
          className="wn-card"
          style={{ background: project.bg }}
        >
          <div data-work-container className="wn-container">
            {/* Background — solid accent colour */}
            <div
              data-work-bg
              className="wn-bg"
              style={{ background: project.bg }}
            />

            {/* Images row */}
            <div className="wn-images">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} data-work-image className="wn-image-col">
                  <div className="wn-image-frame">
                    <img
                      src={`/images/work-new/${project.prefix}-${i}.jpg`}
                      alt={`${project.name} ${i}`}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Text overlay */}
            <div
              style={{
                position: "relative",
                zIndex: 2,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "5rem 4vw 2.5rem",
                pointerEvents: "auto",
                color: "#fff",
                textTransform: "uppercase",
                fontFamily: "'Clash Display', sans-serif",
                fontSize: "clamp(40px, 12.5vw, 150px)",
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: "0.04em",
              }}
            >
              <div style={{ overflow: "hidden" }}>
                <div data-line style={{}}>
                  {project.name}
                </div>
              </div>

              <div style={{ overflow: "hidden", textAlign: "right" }}>
                <div
                  data-line
                  style={{ willChange: "transform", color: project.accent }}
                >
                  {project.subtitle}
                </div>
              </div>
            </div>

            {/* Circle arrow */}
            <Link
              to={`/work/${project.slug}`}
              className="wn-circle-btn"
              style={{
                border: `1.5px solid ${project.accent}`,
                color: project.accent,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 13L13 3M13 3H6M13 3V10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
          {/* /wn-container */}

          <div
            data-work-overlay
            style={{
              position: "absolute",
              inset: 0,
              background: "#000",
              opacity: 0,
              pointerEvents: "none",
              zIndex: 99,
            }}
          />
        </div>
      ))}

      {/* Hero — sticky, sits behind the cards (z-index 0) */}
      <section
        style={{
          position: "sticky",
          top: 0,
          zIndex: 0,
          width: "100vw",
          height: "100vh",
          background: BG,
        }}
      >
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "5rem 3.5vw 2.5rem",
            color: PRIMARY,
            textTransform: "uppercase",
            fontFamily: "'Clash Display', sans-serif",
            fontSize: "clamp(52px, 11vw, 130px)",
            lineHeight: 1,
          }}
        >
          <div>Selected</div>
          <div style={{ textAlign: "center", opacity: 0.4 }}>Scroll</div>
          <div style={{ textAlign: "right" }}>Works</div>
        </div>
      </section>

      {/* Ghost scroll space — JS appends 300vh divs here */}
      <section data-work-section style={{ position: "relative" }} />

      {/* View Experiments — sits above cards (z-index 3) */}
      <section
        style={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          width: "100vw",
          height: "100vh",
          background: BG,
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "5rem 3.5vw 2.5rem",
            color: PRIMARY,
            textTransform: "uppercase",
            fontFamily: "'Clash Display', sans-serif",
            fontSize: "clamp(48px, 11.5vw, 130px)",
            lineHeight: 1,
          }}
        >
          <div>View</div>
          <div style={{ textAlign: "center", opacity: 0.4 }}>—</div>
          <div style={{ textAlign: "right" }}>
            <Link
              to="/experiments"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              Experiments
            </Link>
          </div>
        </div>
      </section>

      {/* Normal-flow footer */}
      <div style={{ position: "relative", zIndex: 2, background: BG }}>
        <Footer />
      </div>
    </div>
  );
}
